import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft,
  Globe,
  Facebook,
  Instagram,
  Clock,
  DollarSign,
  Package,
  Calendar,
  AlertTriangle,
  RefreshCw,
  Info,
  Truck,
  Users
} from 'lucide-react';

const PolicyPage = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');

  const content = language === 'en' ? {
    title: "Policies & Information",
    subtitle: "Important details about our services, fees, and shipping",
    
    shipping: {
      title: "Shipping Timeframes",
      regular: "Regular Shipping: Up to 4 weeks",
      express: "Express Shipping (SHEIN only): 7-10 days",
      note: "Timeframes begin once items arrive at our USA warehouse"
    },
    
    fees: {
      title: "Service Fees",
      standard: "Service Fee: $25 BZD per order",
      bulk: "💡 Save Money: Order in bulk to reduce cost per item",
      partner: "👥 Partners: Significant discounts on bulk orders",
      warehouse: "Ship Directly to Our Warehouse",
      warehouseDesc: "No service fee if you arrange shipping to our USA warehouse yourself",
      warehouseNote: "You handle USA shipping, we handle Belize delivery"
    },
    
    shippingCost: {
      title: "Shipping Costs to Belize",
      single: "Single Item: Starting at $30 BZD",
      bulk: "Bulk Orders: Significant savings per item",
      consolidate: "Order Consolidation",
      consolidateDesc: "Combine multiple orders into one shipment to save on shipping costs",
      example: "Example: 5 items together = much less than 5 separate shipments"
    },
    
    schedule: {
      title: "Shipping Schedule",
      departure: "Weekly Departures: Every Tuesday",
      cutoff: "Order Cutoff: Saturday (3 days before Tuesday)",
      rule: "Only items that arrive at our warehouse by Saturday will be included in Tuesday's shipment",
      nextShip: "Late arrivals will be included in the next week's shipment",
      note: "We ship consolidated orders weekly for efficiency and cost savings"
    },
    
    liability: {
      title: "Liability & Responsibility",
      warehouse: "Items Shipped to Our Warehouse",
      warehouseDesc: "We are not responsible for items lost or damaged during shipping to our USA warehouse",
      recommendation: "Recommendation: Purchase shipping insurance from your chosen carrier",
      ourCare: "Once at Our Warehouse",
      ourCareDesc: "We handle all items with care during storage and shipping to Belize"
    },
    
    refunds: {
      title: "Refund Policy",
      vary: "Refunds are evaluated on a case-by-case basis",
      qualify: "May Qualify for 50-100% Refund:",
      qualifyItems: [
        "Wrong item received",
        "Significantly not as described",
        "Defective items (manufacturer defect)"
      ],
      noQualify: "May NOT Qualify for Refund:",
      noQualifyItems: [
        "Change of mind",
        "Size/fit issues",
        "Color differences from screen",
        "Items lost/damaged before reaching our warehouse"
      ],
      delays: "Shipping Delays",
      delaysDesc: "No refunds for delayed shipments. Delays can occur due to customs, carrier issues, or high volume periods."
    },
    
    tips: {
      title: "Money-Saving Tips",
      tip1: "Order in Bulk",
      tip1desc: "Combine multiple items to reduce per-item cost",
      tip2: "Join as a Partner",
      tip2desc: "Get wholesale rates and special discounts",
      tip3: "Ship to Our Warehouse",
      tip3desc: "Save $25 service fee by arranging USA shipping yourself",
      tip4: "Group Orders",
      tip4desc: "Coordinate with friends/family to share shipping costs"
    }
  } : {
    title: "Políticas e Información",
    subtitle: "Detalles importantes sobre nuestros servicios, tarifas y envíos",
    
    shipping: {
      title: "Tiempos de Envío",
      regular: "Envío Regular: Hasta 4 semanas",
      express: "Envío Exprés (solo SHEIN): 7-10 días",
      note: "Los plazos comienzan una vez que los artículos llegan a nuestro almacén en EE.UU."
    },
    
    fees: {
      title: "Tarifas de Servicio",
      standard: "Tarifa de Servicio: $25 BZD por pedido",
      bulk: "💡 Ahorre Dinero: Ordene al por mayor para reducir el costo por artículo",
      partner: "👥 Socios: Descuentos significativos en pedidos al por mayor",
      warehouse: "Envíe Directamente a Nuestro Almacén",
      warehouseDesc: "Sin tarifa de servicio si organiza el envío a nuestro almacén en EE.UU. usted mismo",
      warehouseNote: "Usted maneja el envío en EE.UU., nosotros manejamos la entrega en Belice"
    },
    
    shippingCost: {
      title: "Costos de Envío a Belice",
      single: "Artículo Individual: Desde $30 BZD",
      bulk: "Pedidos al Por Mayor: Ahorros significativos por artículo",
      consolidate: "Consolidación de Pedidos",
      consolidateDesc: "Combine múltiples pedidos en un envío para ahorrar en costos de envío",
      example: "Ejemplo: 5 artículos juntos = mucho menos que 5 envíos separados"
    },
    
    schedule: {
      title: "Calendario de Envíos",
      departure: "Salidas Semanales: Cada Martes",
      cutoff: "Fecha Límite de Pedido: Sábado (3 días antes del martes)",
      rule: "Solo los artículos que lleguen a nuestro almacén antes del sábado se incluirán en el envío del martes",
      nextShip: "Las llegadas tardías se incluirán en el envío de la próxima semana",
      note: "Enviamos pedidos consolidados semanalmente para eficiencia y ahorro de costos"
    },
    
    liability: {
      title: "Responsabilidad",
      warehouse: "Artículos Enviados a Nuestro Almacén",
      warehouseDesc: "No somos responsables de artículos perdidos o dañados durante el envío a nuestro almacén en EE.UU.",
      recommendation: "Recomendación: Compre seguro de envío del transportista elegido",
      ourCare: "Una Vez en Nuestro Almacén",
      ourCareDesc: "Manejamos todos los artículos con cuidado durante el almacenamiento y envío a Belice"
    },
    
    refunds: {
      title: "Política de Reembolsos",
      vary: "Los reembolsos se evalúan caso por caso",
      qualify: "Puede Calificar para Reembolso del 50-100%:",
      qualifyItems: [
        "Artículo incorrecto recibido",
        "Significativamente diferente a la descripción",
        "Artículos defectuosos (defecto de fabricante)"
      ],
      noQualify: "Puede NO Calificar para Reembolso:",
      noQualifyItems: [
        "Cambio de opinión",
        "Problemas de talla/ajuste",
        "Diferencias de color de la pantalla",
        "Artículos perdidos/dañados antes de llegar a nuestro almacén"
      ],
      delays: "Retrasos en el Envío",
      delaysDesc: "Sin reembolsos por envíos retrasados. Los retrasos pueden ocurrir debido a aduanas, problemas del transportista o períodos de alto volumen."
    },
    
    tips: {
      title: "Consejos para Ahorrar Dinero",
      tip1: "Ordene al Por Mayor",
      tip1desc: "Combine múltiples artículos para reducir el costo por artículo",
      tip2: "Únase como Socio",
      tip2desc: "Obtenga tarifas mayoristas y descuentos especiales",
      tip3: "Envíe a Nuestro Almacén",
      tip3desc: "Ahorre $25 de tarifa de servicio organizando el envío en EE.UU. usted mismo",
      tip4: "Agrupe Pedidos",
      tip4desc: "Coordine con amigos/familia para compartir costos de envío"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f7f5f2] to-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/90 backdrop-blur-md shadow-sm z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#8b1538] hover:text-[#6b0f2a]"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">
                {language === 'en' ? 'Back to Home' : 'Volver'}
              </span>
            </Button>
            
            <img 
              src="https://customer-assets.emergentagent.com/job_57e2dd96-4e50-451b-8abf-042890e7a51b/artifacts/m648krxt_Athena%27s%20Logo.png"
              alt="Athena's"
              className="h-12 w-auto object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
              className="flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'ES' : 'EN'}
            </Button>
            <a 
              href="https://www.facebook.com/profile.php?id=61584846392424" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a 
              href="https://www.instagram.com/athenasonlineshoppingservices/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-5xl">
          {/* Title */}
          <div className="text-center mb-12">
            <Info className="w-16 h-16 text-[#8b1538] mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-4">
              {content.title}
            </h1>
            <p className="text-lg text-[#736c64]">{content.subtitle}</p>
          </div>

          {/* Shipping Timeframes */}
          <Card className="mb-6 border-2 border-[#3dd3ee]/30">
            <CardHeader className="bg-gradient-to-r from-[#3dd3ee]/10 to-transparent">
              <CardTitle className="flex items-center gap-3 text-[#1e1919]">
                <Clock className="w-6 h-6 text-[#3dd3ee]" />
                {content.shipping.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-[#8b1538] flex-shrink-0 mt-0.5" />
                  <span className="text-[#1e1919]">{content.shipping.regular}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Truck className="w-5 h-5 text-[#fad24b] flex-shrink-0 mt-0.5" />
                  <span className="text-[#1e1919] font-semibold">{content.shipping.express}</span>
                </li>
              </ul>
              <p className="text-sm text-[#736c64] mt-4 bg-[#f7f5f2] p-3 rounded-lg">
                ℹ️ {content.shipping.note}
              </p>
            </CardContent>
          </Card>

          {/* Service Fees */}
          <Card className="mb-6 border-2 border-[#8b1538]/30">
            <CardHeader className="bg-gradient-to-r from-[#8b1538]/10 to-transparent">
              <CardTitle className="flex items-center gap-3 text-[#1e1919]">
                <DollarSign className="w-6 h-6 text-[#8b1538]" />
                {content.fees.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-[#8b1538]/5 p-4 rounded-lg">
                  <p className="text-[#1e1919] font-bold text-lg">{content.fees.standard}</p>
                </div>
                <p className="text-[#736c64]">{content.fees.bulk}</p>
                <p className="text-[#736c64]">{content.fees.partner}</p>
                
                <div className="border-t-2 border-[#fad24b] my-4"></div>
                
                <div className="bg-[#fad24b]/10 p-4 rounded-lg">
                  <h4 className="font-bold text-[#1e1919] mb-2">{content.fees.warehouse}</h4>
                  <p className="text-[#736c64] mb-2">{content.fees.warehouseDesc}</p>
                  <p className="text-sm text-[#736c64] italic">{content.fees.warehouseNote}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipping Costs */}
          <Card className="mb-6 border-2 border-[#ff8c19]/30">
            <CardHeader className="bg-gradient-to-r from-[#ff8c19]/10 to-transparent">
              <CardTitle className="flex items-center gap-3 text-[#1e1919]">
                <Package className="w-6 h-6 text-[#ff8c19]" />
                {content.shippingCost.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-3">
                <p className="text-[#1e1919]">{content.shippingCost.single}</p>
                <p className="text-[#1e1919] font-semibold">{content.shippingCost.bulk}</p>
                
                <div className="bg-[#3dd3ee]/10 p-4 rounded-lg mt-4">
                  <h4 className="font-bold text-[#1e1919] mb-2">{content.shippingCost.consolidate}</h4>
                  <p className="text-[#736c64] mb-2">{content.shippingCost.consolidateDesc}</p>
                  <p className="text-sm text-[#736c64] italic">{content.shippingCost.example}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipping Schedule */}
          <Card className="mb-6 border-2 border-[#fad24b]/30">
            <CardHeader className="bg-gradient-to-r from-[#fad24b]/10 to-transparent">
              <CardTitle className="flex items-center gap-3 text-[#1e1919]">
                <Calendar className="w-6 h-6 text-[#fad24b]" />
                {content.schedule.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#8b1538] rounded-full mt-2"></div>
                  <div>
                    <p className="font-bold text-[#1e1919]">{content.schedule.departure}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#8b1538] rounded-full mt-2"></div>
                  <div>
                    <p className="font-bold text-[#1e1919]">{content.schedule.cutoff}</p>
                  </div>
                </div>
                <div className="bg-[#f7f5f2] p-4 rounded-lg">
                  <p className="text-[#1e1919] mb-2">{content.schedule.rule}</p>
                  <p className="text-sm text-[#736c64]">{content.schedule.nextShip}</p>
                </div>
                <p className="text-sm text-[#736c64] italic">💡 {content.schedule.note}</p>
              </div>
            </CardContent>
          </Card>

          {/* Liability */}
          <Card className="mb-6 border-2 border-[#ff8c19]/30">
            <CardHeader className="bg-gradient-to-r from-[#ff8c19]/10 to-transparent">
              <CardTitle className="flex items-center gap-3 text-[#1e1919]">
                <AlertTriangle className="w-6 h-6 text-[#ff8c19]" />
                {content.liability.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h4 className="font-bold text-[#1e1919] mb-2">{content.liability.warehouse}</h4>
                  <p className="text-[#736c64]">{content.liability.warehouseDesc}</p>
                  <p className="text-sm text-red-600 mt-2 font-semibold">{content.liability.recommendation}</p>
                </div>
                
                <div className="bg-green-50 border-l-4 border-green-500 p-4">
                  <h4 className="font-bold text-[#1e1919] mb-2">{content.liability.ourCare}</h4>
                  <p className="text-[#736c64]">{content.liability.ourCareDesc}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Refund Policy */}
          <Card className="mb-6 border-2 border-[#8b1538]/30">
            <CardHeader className="bg-gradient-to-r from-[#8b1538]/10 to-transparent">
              <CardTitle className="flex items-center gap-3 text-[#1e1919]">
                <RefreshCw className="w-6 h-6 text-[#8b1538]" />
                {content.refunds.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-[#736c64] mb-4 italic">{content.refunds.vary}</p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-bold text-green-700 mb-3">{content.refunds.qualify}</h4>
                  <ul className="space-y-2">
                    {content.refunds.qualifyItems.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-[#736c64]">
                        <span className="text-green-600">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-bold text-red-700 mb-3">{content.refunds.noQualify}</h4>
                  <ul className="space-y-2">
                    {content.refunds.noQualifyItems.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-[#736c64]">
                        <span className="text-red-600">✗</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <h4 className="font-bold text-[#1e1919] mb-2">{content.refunds.delays}</h4>
                <p className="text-sm text-[#736c64]">{content.refunds.delaysDesc}</p>
              </div>
            </CardContent>
          </Card>

          {/* Money-Saving Tips */}
          <Card className="border-2 border-[#3dd3ee]/30">
            <CardHeader className="bg-gradient-to-r from-[#3dd3ee]/10 to-transparent">
              <CardTitle className="flex items-center gap-3 text-[#1e1919]">
                <DollarSign className="w-6 h-6 text-[#3dd3ee]" />
                {content.tips.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#fad24b]/10 p-4 rounded-lg">
                  <h4 className="font-bold text-[#1e1919] mb-2">{content.tips.tip1}</h4>
                  <p className="text-sm text-[#736c64]">{content.tips.tip1desc}</p>
                </div>
                <div className="bg-[#ff8c19]/10 p-4 rounded-lg">
                  <h4 className="font-bold text-[#1e1919] mb-2">{content.tips.tip2}</h4>
                  <p className="text-sm text-[#736c64]">{content.tips.tip2desc}</p>
                </div>
                <div className="bg-[#3dd3ee]/10 p-4 rounded-lg">
                  <h4 className="font-bold text-[#1e1919] mb-2">{content.tips.tip3}</h4>
                  <p className="text-sm text-[#736c64]">{content.tips.tip3desc}</p>
                </div>
                <div className="bg-[#8b1538]/10 p-4 rounded-lg">
                  <h4 className="font-bold text-[#1e1919] mb-2">{content.tips.tip4}</h4>
                  <p className="text-sm text-[#736c64]">{content.tips.tip4desc}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1e1919] text-white py-8 px-4">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-4">
            <a
              href="https://www.facebook.com/profile.php?id=61584846392424"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              className="text-[#fad24b] hover:text-white transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a
              href="https://www.instagram.com/athenasonlineshoppingservices/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="text-[#fad24b] hover:text-white transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
          <p className="text-[#bbb5ae] text-sm mb-3">
            Business Hours: 10 AM - 9 PM (7 Days)
          </p>
          <div className="mb-4 text-sm space-y-1">
            <p className="text-[#bbb5ae]">
              Phone: <a href="tel:+5016735717" data-testid="footer-phone-link" className="text-[#fad24b] hover:text-white transition-colors">6735717</a>
            </p>
            <p className="text-[#bbb5ae]">
              WhatsApp: <a href="https://wa.me/5016735717" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="text-[#fad24b] hover:text-white transition-colors">Chat with us</a>
            </p>
            <p className="text-[#bbb5ae]">
              Email: <a href="mailto:athenasonlineshoppingservices@gmail.com" data-testid="footer-email-link" className="text-[#fad24b] hover:text-white transition-colors break-all">athenasonlineshoppingservices@gmail.com</a>
            </p>
            <p className="text-[#bbb5ae]">
              <a href="/privacy" data-testid="footer-privacy-link" className="text-[#fad24b] hover:text-white transition-colors">Privacy Policy</a>
            </p>
          </div>
          <p className="text-[#bbb5ae] text-sm">
            © {new Date().getFullYear()} Athena's Online Shopping Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PolicyPage;
